import { supabase } from '@/lib/supabase';

export interface UserAddress {
  id: string;
  user_id: string;
  alias: string;
  address: string;
  detail_address?: string;
  latitude?: number;
  longitude?: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserAddressCreate {
  alias: string;
  address: string;
  detail_address?: string;
  latitude?: number;
  longitude?: number;
}

class UserAddressService {
  
  /**
   * 사용자의 주소 목록 가져오기
   */
  async getUserAddresses(userId: string): Promise<{ success: boolean; data?: UserAddress[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('user_addresses')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Get user addresses error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Get user addresses error:', error);
      return { success: false, error: '주소 목록을 불러오는 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 사용자의 선택된 주소 가져오기
   */
  async getSelectedAddress(userId: string): Promise<{ success: boolean; data?: UserAddress; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('user_addresses')
        .select('*')
        .eq('user_id', userId)
        .eq('is_default', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned - this is ok
          return { success: true, data: undefined };
        }
        console.error('Get selected address error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Get selected address error:', error);
      return { success: false, error: '선택된 주소를 불러오는 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 새 주소 추가 (최대 5개 제한)
   */
  async addAddress(userId: string, addressData: UserAddressCreate): Promise<{ success: boolean; data?: UserAddress; error?: string }> {
    try {
      // 먼저 현재 주소 개수 확인
      const { data: existingAddresses, error: countError } = await supabase
        .from('user_addresses')
        .select('id')
        .eq('user_id', userId);

      if (countError) {
        console.error('Count addresses error:', countError);
        return { success: false, error: countError.message };
      }

      if (existingAddresses && existingAddresses.length >= 5) {
        return { success: false, error: '사용자당 최대 5개의 주소만 등록할 수 있습니다.' };
      }

      // 첫 번째 주소인 경우 자동으로 선택된 주소로 설정
      const isFirstAddress = !existingAddresses || existingAddresses.length === 0;

      const { data, error } = await supabase
        .from('user_addresses')
        .insert({
          user_id: userId,
          alias: addressData.alias,
          address: addressData.address,
          detail_address: addressData.detail_address,
          latitude: addressData.latitude,
          longitude: addressData.longitude,
          is_default: isFirstAddress
        })
        .select()
        .single();

      if (error) {
        console.error('Add address error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Add address error:', error);
      return { success: false, error: '주소 추가 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 주소 선택 (다른 주소들은 자동으로 선택 해제)
   */
  async selectAddress(userId: string, addressId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // 먼저 모든 주소를 선택 해제
      await supabase
        .from('user_addresses')
        .update({ is_default: false, updated_at: new Date().toISOString() })
        .eq('user_id', userId);

      // 선택한 주소만 선택 상태로 변경
      const { error } = await supabase
        .from('user_addresses')
        .update({ is_default: true, updated_at: new Date().toISOString() })
        .eq('id', addressId)
        .eq('user_id', userId);

      if (error) {
        console.error('Select address error:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Select address error:', error);
      return { success: false, error: '주소 선택 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 주소 삭제
   */
  async deleteAddress(userId: string, addressId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // 먼저 삭제할 주소 정보 가져오기
      const { data: addressToDelete, error: getError } = await supabase
        .from('user_addresses')
        .select('is_default')
        .eq('id', addressId)
        .eq('user_id', userId)
        .single();

      if (getError) {
        console.error('Get address to delete error:', getError);
        return { success: false, error: getError.message };
      }

      // 주소 삭제
      const { error: deleteError } = await supabase
        .from('user_addresses')
        .delete()
        .eq('id', addressId)
        .eq('user_id', userId);

      if (deleteError) {
        console.error('Delete address error:', deleteError);
        return { success: false, error: deleteError.message };
      }

      // 삭제된 주소가 선택된 주소였다면, 다른 주소 중 하나를 자동으로 선택
      if (addressToDelete.is_default) {
        const { data: remainingAddresses } = await supabase
          .from('user_addresses')
          .select('id')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1);

        if (remainingAddresses && remainingAddresses.length > 0) {
          await this.selectAddress(userId, remainingAddresses[0].id);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Delete address error:', error);
      return { success: false, error: '주소 삭제 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 주소 정보 업데이트
   */
  async updateAddress(
    userId: string, 
    addressId: string, 
    updates: Partial<UserAddressCreate>
  ): Promise<{ success: boolean; data?: UserAddress; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('user_addresses')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', addressId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        console.error('Update address error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Update address error:', error);
      return { success: false, error: '주소 업데이트 중 오류가 발생했습니다.' };
    }
  }
}

export const userAddressService = new UserAddressService();